import {
  CheckableAvatarSelectItem,
  CheckableSelectItem,
  CheckableTagSelectItem,
  Select,
  TagSelectSuggestion,
} from '@dixa/lib-shared-habla';
import type {
  ReadSearchApiArg,
  SearchCompany,
  SearchFilterCondition,
  SearchFilterConditionArray,
  SearchFilterConditionCustomAttributeText,
  SearchFilterConditionString,
  SearchFilterConditionText,
  SearchFilterField,
  SearchQueue,
  SearchResult,
  SearchScope,
  SearchTag,
  SearchUser,
} from 'lib-search-types';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { Channel } from 'lib-shared-types-channels';
import map from 'lodash/fp/map';
import {
  ComponentType,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { getChannelFeatureFlagsStatus } from 'utils/featureFlagging/channelFeatureFlags';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { getUserDisplayName } from 'utils/user';

import {
  filterItems,
  FilterItemType,
} from '../../../../../../utils/definitions';
import { ConditionFieldProps } from '../../conditionField';

import { useConditionFieldSelect } from './utils/useConditionFieldSelect';

type SearchResultInner<TScope extends SearchScope> =
  SearchResult<TScope>['result'];

const defaultItemFormatter = map<SearchQueue | SearchCompany, FilterItemType>(
  ({ id, name }) => ({ id, name }),
);

const tagItemFormatter = map<SearchTag, FilterItemType>(
  ({ id, name, color }) => ({
    id,
    name,
    color: color ?? undefined,
  }),
);

const userItemFormatter = map<SearchUser, FilterItemType>((user) => ({
  id: user.id,
  name: getUserDisplayName(user),
  avatar_url: user.avatar_url,
}));

type CreateFreeTextFilterConfig = {
  readonly emptyMessage: string;
  readonly renderSuggestion: (input: string) => string;
  readonly transformSuggestion: (input: string) => FilterItemType | null;
};

function createFreeTextFilter<
  TCondition extends SearchFilterCondition<'conversations'>,
>({
  emptyMessage,
  renderSuggestion,
  transformSuggestion,
}: CreateFreeTextFilterConfig): FunctionComponent<
  ConditionFieldProps<TCondition>
> {
  function Filter({
    condition,
    onChange,
    ...props
  }: ConditionFieldProps<TCondition>): JSX.Element {
    const { handleChange, handleClear, mapValuesToItems } =
      useConditionFieldSelect({ condition, onChange });

    const [searchTerm, setSearchTerm] = useState('');
    const selectedItems = mapValuesToItems();

    // When there is a search term, don't render the empty state.
    const emptyComponent = searchTerm ? () => null : undefined;

    /** Handles adding a new free-text value to the selection. */
    const handleAddSuggestion = ({ id }: FilterItemType) => {
      const item = transformSuggestion(id.toString());

      if (item) {
        handleChange(item);
      }
      setSearchTerm('');
    };

    return (
      <Select<FilterItemType>
        multiple
        clearable
        searchable
        placeholder="Type a value and press Enter"
        items={[]}
        selectedItems={selectedItems}
        onChange={handleAddSuggestion}
        onClear={handleClear}
        hideSelectedItems
        limit={3}
        disabled={props.disabled}
        onSearch={setSearchTerm}
        suggestionComponent={TagSelectSuggestion}
        onSuggest={(suggestion: string) =>
          handleAddSuggestion({ id: suggestion, name: suggestion })
        }
        renderSuggestion={renderSuggestion}
        emptyMessage={emptyMessage}
        emptyComponent={emptyComponent}
      />
    );
  }

  return Filter;
}

function createStaticFilter<
  TCondition extends SearchFilterCondition<'conversations'>,
>(
  field: SearchFilterField<'conversations'>,
): FunctionComponent<ConditionFieldProps<TCondition>> {
  function Filter({
    condition,
    onChange,
    ...props
  }: ConditionFieldProps<TCondition>): JSX.Element {
    const { handleChange, handleClear, mapValuesToItems } =
      useConditionFieldSelect({ condition, onChange });

    const flags = useFeatureFlag(['instagram-management']);
    const channelFlags = getChannelFeatureFlagsStatus();

    const items = useMemo(() => {
      return field === 'initial_channel'
        ? filterItems[field].filter(({ id: channel }) => {
            const casedChannel = convertChannelCasing(
              channel as string,
              'snake',
            ) as Channel;
            if (casedChannel === 'facebook_messenger') {
              // Instagram conversations appear as Facebook conversations
              return (
                channelFlags.get(casedChannel) || flags.instagramManagement
              );
            }
            return channelFlags.get(casedChannel);
          })
        : filterItems[field];
    }, [flags, channelFlags]);

    const selectedItems = mapValuesToItems();

    const { placeholder, disabled } = props;

    return (
      <Select
        multiple
        clearable
        searchable
        placeholder={placeholder}
        items={items}
        selectedItems={selectedItems}
        onChange={handleChange}
        onClear={handleClear}
        hideSelectedItems
        limit={3}
        disabled={disabled}
      />
    );
  }

  return Filter;
}

type CreateDynamicFilterConfig<TScope extends SearchScope> = {
  readonly arg: ReadSearchApiArg<TScope>;
  readonly itemComponent: ComponentType<typeof CheckableSelectItem>;
  readonly itemFormatter: (
    results: ReadonlyArray<SearchResultInner<TScope>>,
  ) => FilterItemType[];
  readonly prepareDisabled?: (
    selectedItems: FilterItemType[],
    items: FilterItemType[],
  ) => FilterItemType['id'][];
};

function createDynamicFilter<
  TScope extends SearchScope,
  TCondition extends SearchFilterCondition<'conversations'>,
>({
  arg,
  itemComponent = CheckableSelectItem,
  itemFormatter,
  prepareDisabled,
}: CreateDynamicFilterConfig<TScope>): FunctionComponent<
  ConditionFieldProps<TCondition>
> {
  function Filter({
    condition,
    placeholder,
    disabled,
    onChange,
  }: ConditionFieldProps<TCondition>) {
    const [searchTerm, setSearchTerm] = useState<string>();

    const [readSearch, { results, isFetching, hasNextPage, fetchNextPage }] =
      useLazyReadSearchInfiniteQuery<TScope>();

    useEffect(() => {
      if (searchTerm !== undefined) {
        readSearch(
          {
            ...arg,
            query: searchTerm,
          },
          { debounceTime: 300 },
        );
      }
    }, [readSearch, searchTerm]);

    const { handleChange, handleClear, mapValuesToItems } =
      useConditionFieldSelect<TCondition>({ condition, onChange });

    const handleSearch = useCallback((value?: string) => {
      setSearchTerm(value?.trim());
    }, []);

    const handleToggle = useCallback(
      (isOpen: boolean) => {
        if (isOpen && !results.length) {
          readSearch({
            ...arg,
            query: searchTerm,
          });
        } else if (!isOpen && searchTerm !== '') {
          setSearchTerm('');
        }
      },
      [readSearch, results.length, searchTerm],
    );

    const items = itemFormatter(results);
    const selectedItems = useMemo(() => mapValuesToItems(), [mapValuesToItems]);

    return (
      <Select
        multiple
        clearable
        searchable
        placeholder={placeholder}
        onSearch={handleSearch}
        onChange={handleChange}
        onClear={handleClear}
        onToggle={handleToggle}
        pagination={{
          infinite: true,
          nextEnable: hasNextPage,
          previousEnable: false,
          onNext: () => Promise.resolve(fetchNextPage()),
        }}
        items={items}
        selectedItems={selectedItems}
        disabledItems={prepareDisabled?.(selectedItems, items)}
        itemComponent={itemComponent}
        loading={isFetching}
        limit={3}
        disabled={disabled}
      />
    );
  }

  return Filter;
}

export const ConditionFieldId = createFreeTextFilter<
  SearchFilterConditionString<'numeric_id'>
>({
  emptyMessage: 'Type the numeric conversation ID (e.g. 123456)',
  renderSuggestion: (input: string) => `Add "#${input.replace('#', '')}"`,
  transformSuggestion: (input: string) => {
    const id = input.replace('#', '');
    const isValid = /^[0-9]*$/.test(id);
    return isValid ? { id, name: `#${id}` } : null;
  },
});

export const ConditionFieldText = createFreeTextFilter<
  SearchFilterConditionText<'note_message'>
>({
  emptyMessage: 'Enter the text to match...',
  renderSuggestion: (input: string) => `Add "${input}"`,
  transformSuggestion: (input: string) => ({ id: input, name: input }),
});

export const ConditionFieldCustomAttributeText = createFreeTextFilter<
  | SearchFilterConditionCustomAttributeText<'conversation_custom_attribute'>
  | SearchFilterConditionCustomAttributeText<'contact_custom_attribute'>
>({
  emptyMessage: 'Enter any custom attribute value (e.g. ID-123)',
  renderSuggestion: (text: string) => `Add "${text}"`,
  transformSuggestion: (input: string) => ({ id: input, name: input }),
});

export const ConditionFieldDirection =
  createStaticFilter<SearchFilterConditionString<'initial_direction'>>(
    'initial_direction',
  );
export const ConditionFieldStatus =
  createStaticFilter<SearchFilterConditionString<'status'>>('status');
export const ConditionFieldChannel =
  createStaticFilter<SearchFilterConditionString<'initial_channel'>>(
    'initial_channel',
  );
export const ConditionFieldUserType =
  createStaticFilter<SearchFilterConditionString<'user_type'>>('user_type');

export const ConditionFieldQueue = createDynamicFilter<
  'queues',
  SearchFilterConditionString<'queue_id', true>
>({
  arg: {
    scope: 'queues',
    filters: {
      strategy: 'all',
      filters: [
        {
          field: 'is_archived',
          operator: 'not_equals',
          values: [{ value: 'true' }],
        },
      ],
    },
    sortBy: { field: 'name', order: 'asc' },
  },
  itemComponent: CheckableSelectItem,
  itemFormatter: defaultItemFormatter,
});

export const ConditionFieldContacts = createDynamicFilter<
  'users',
  SearchFilterConditionString<'requested_by'>
>({
  arg: {
    scope: 'users',
    sortBy: { field: 'display_name', order: 'asc' },
    filters: {
      strategy: 'all',
      filters: [
        {
          field: 'user_type',
          operator: 'is_one',
          values: [{ value: 'contact' }],
        },
      ],
    },
  },
  itemComponent: CheckableAvatarSelectItem,
  itemFormatter: userItemFormatter,
});

export const ConditionFieldAgents = createDynamicFilter<
  'users',
  SearchFilterConditionString<'agent_id', true>
>({
  arg: {
    scope: 'users',
    sortBy: { field: 'display_name', order: 'asc' },
    filters: {
      strategy: 'all',
      filters: [
        {
          field: 'user_type',
          operator: 'is_one',
          values: [{ value: 'member' }],
        },
      ],
    },
  },
  itemComponent: CheckableAvatarSelectItem,
  itemFormatter: userItemFormatter,
});

export const ConditionFieldTags = createDynamicFilter<
  'tags',
  SearchFilterConditionArray<'tags', true>
>({
  arg: {
    scope: 'tags',
    sortBy: { field: 'name', order: 'asc' },
  },
  itemComponent: CheckableTagSelectItem,
  itemFormatter: tagItemFormatter,
  prepareDisabled: (_, tags) =>
    tags
      .filter((tag) => (tag as SearchTag).is_deactivated)
      .map((tag) => tag.id),
});

export const ConditionFieldCompany = createDynamicFilter<
  'companies',
  SearchFilterConditionString<'company_id', true>
>({
  arg: {
    scope: 'companies',
    sortBy: { field: 'name', order: 'asc' },
  },
  itemComponent: CheckableSelectItem,
  itemFormatter: defaultItemFormatter,
});
